import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api";
import type {
	CreateFaqArticleRequest,
	SupportTicketStatus,
	TicketMessageRequest,
	UpdateFaqArticleRequest,
} from "@/api";

// Центр помощи (Э5, fd-support-01). Две независимые сущности — FAQ-база знаний
// и обращения. После каждой мутации инвалидируем соответствующий срез: FAQ —
// весь список; тикет — и деталь, и списки (счётчики/статусы в списке зависят
// от переписки).

export const supportKeys = {
	all: ["support"] as const,
	faq: ["support", "faq"] as const,
	tickets: (status?: SupportTicketStatus) =>
		["support", "tickets", status ?? "all"] as const,
	ticket: (id: string) => ["support", "ticket", id] as const,
};

// --- FAQ --------------------------------------------------------------------

export function useFaqArticles() {
	return useQuery({
		queryKey: supportKeys.faq,
		queryFn: () => api.support.faq(),
	});
}

export function useCreateFaqArticle() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateFaqArticleRequest) => api.support.createFaq(input),
		onSettled: () => queryClient.invalidateQueries({ queryKey: supportKeys.faq }),
	});
}

export function useUpdateFaqArticle() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, input }: { id: string; input: UpdateFaqArticleRequest }) =>
			api.support.updateFaq(id, input),
		onSettled: () => queryClient.invalidateQueries({ queryKey: supportKeys.faq }),
	});
}

export function useDeleteFaqArticle() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.support.deleteFaq(id),
		onSettled: () => queryClient.invalidateQueries({ queryKey: supportKeys.faq }),
	});
}

// --- Обращения (тикеты) -----------------------------------------------------

export function useSupportTickets(status?: SupportTicketStatus) {
	return useQuery({
		queryKey: supportKeys.tickets(status),
		queryFn: () => api.support.tickets(status),
	});
}

export function useSupportTicket(id: string | null) {
	return useQuery({
		queryKey: supportKeys.ticket(id ?? ""),
		queryFn: () => api.support.ticket(id ?? ""),
		enabled: Boolean(id),
	});
}

function useTicketMutation<V>(mutationFn: (vars: V) => Promise<unknown>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		// Ответ и смена статуса меняют и деталь, и позиции в списках — обновляем
		// весь срез support, чтобы список статусов не разошёлся с тредом.
		onSettled: () => queryClient.invalidateQueries({ queryKey: supportKeys.all }),
	});
}

export function useReplyTicket() {
	return useTicketMutation<{ id: string; input: TicketMessageRequest }>(({ id, input }) =>
		api.support.reply(id, input),
	);
}

export function useSetTicketStatus() {
	return useTicketMutation<{ id: string; status: SupportTicketStatus }>(({ id, status }) =>
		api.support.setStatus(id, status),
	);
}
